import { Button } from '@daily/shared/components/Button';
import { Card, CardContent } from '@daily/shared/components/Card';
import { BreakoutIcon } from '@daily/shared/components/Icons/BreakoutIcon';
import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { useDaily, useLocalSessionId } from '@daily-co/daily-react';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BreakoutsSessionData,
  useBreakouts,
} from '/contexts/BreakoutsProvider';
import { useBreakoutView } from '/lib/state/breakouts';

import { ConfigureBreakoutRooms } from './ConfigureBreakoutRooms';
import { useBreakoutSessionState } from './hooks/useBreakoutSessionState';
import { useRoomMapping } from './hooks/useRoomMapping';
import { useRooms } from './hooks/useRooms';
import { useSelectParticipant } from './hooks/useSelectParticipant';
import { OngoingBreakoutSession } from './OngoingBreakoutSession';

export const BreakoutRooms: React.FC<React.PropsWithChildren> = () => {
  const daily = useDaily();
  const { t } = useTranslation();
  const [breakoutView, setBreakoutView] = useBreakoutView();
  const { breakoutHost, breakoutMapping, breakoutStatus } =
    useBreakoutSessionState();
  const { setBreakoutHost } = useBreakouts();
  const localSessionId = useLocalSessionId();
  const { addRoom, deleteAllRooms, deleteRoom, rooms } = useRooms();
  const { deselectAllParticipants } = useSelectParticipant();
  const { resetRoomMapping, syncRoomMappingFromSessionState } =
    useRoomMapping();

  const canConfigureBreakouts =
    !breakoutHost || breakoutHost === localSessionId;

  const createSession = useCallback(() => {
    deleteAllRooms();
    deselectAllParticipants();
    resetRoomMapping();
    setBreakoutHost();
    setBreakoutView('configure');
  }, [
    deleteAllRooms,
    deselectAllParticipants,
    resetRoomMapping,
    setBreakoutHost,
    setBreakoutView,
  ]);

  const recreateSession = useCallback(async () => {
    // In case local rooms were deleted previously, restore them from session state
    if (rooms.some((id) => !(id in breakoutMapping))) {
      rooms.forEach(deleteRoom);
      Object.keys(breakoutMapping).forEach(addRoom);
    }
    setBreakoutHost();
    const data = daily.meetingSessionState().data as BreakoutsSessionData;
    await syncRoomMappingFromSessionState(data);
    setBreakoutView('configure');
  }, [
    addRoom,
    breakoutMapping,
    daily,
    deleteRoom,
    rooms,
    setBreakoutHost,
    setBreakoutView,
    syncRoomMappingFromSessionState,
  ]);

  /**
   * Automatically switch view.
   */
  useEffect(() => {
    if (
      breakoutStatus === 'ongoing' &&
      breakoutHost === localSessionId &&
      breakoutView !== 'ongoing'
    ) {
      setBreakoutView('ongoing');
    }
  }, [
    breakoutHost,
    breakoutStatus,
    breakoutView,
    localSessionId,
    setBreakoutView,
  ]);

  return (
    <div className="breakout-rooms-panel">
      {breakoutView === 'start' && (
        <Card noBorder shadow="none">
          <CardContent>
            <Stack gap={24}>
              <Text>{t('breakouts.configurationDesc')}</Text>
              <Stack gap={32}>
                {!canConfigureBreakouts && (
                  <Card appearance="grey" shadow="none">
                    <CardContent
                      className="host-info-box-content"
                      style={{ padding: 8 }}
                    >
                      <BreakoutIcon />
                      <Text>{t('breakouts.breakoutSessionInProgress')}</Text>
                    </CardContent>
                  </Card>
                )}
                <Stack gap={16}>
                  <Button
                    id="breakouts-create-new-session"
                    disabled={!canConfigureBreakouts}
                    fullWidth
                    onClick={createSession}
                  >
                    {t('breakouts.createNewSession')}
                  </Button>
                  {canConfigureBreakouts &&
                    Object.keys(breakoutMapping).length > 0 && (
                      <Button
                        id="breakouts-recreate-session"
                        disabled={!canConfigureBreakouts}
                        fullWidth
                        onClick={recreateSession}
                        variant="secondary"
                      >
                        {t('breakouts.recreateSession')}
                      </Button>
                    )}
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {breakoutView === 'configure' && <ConfigureBreakoutRooms />}
      {breakoutView === 'ongoing' && <OngoingBreakoutSession />}
      {/*needs to be in the DOM somewhere for both the ongoing and configure breakout panels,
      so it can be used in the Participants component */}
      <Button className="drag-image" id="drag-image" tabIndex={-1}>
        {t('breakouts.multipleSelected')}
      </Button>
      <style jsx>{`
        .breakout-rooms-panel {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .breakout-rooms-panel :global(.drag-image) {
          border-radius: 4px;
          clip: rect(0px, 0px, 0px, 0px);
          left: 0;
          position: fixed;
          top: 0;
          z-index: -2; // needs to be on screen, but not visible
        }
        .breakout-rooms-panel :global(.host-info-box-content) {
          align-items: center;
          display: flex;
          flex-direction: row;
        }
        .breakout-rooms-panel :global(.host-info-box-content p) {
          margin-left: 4px;
        }
      `}</style>
    </div>
  );
};
